import * as v from "valibot";

import { sendSlackSchemaMismatchReport } from "src/helpers/slack";

/**
 * valibot schema builders that fill a type-appropriate default when a field is
 * missing (or fails validation) instead of throwing:
 *
 *   string  -> ""      number -> 0      boolean -> false      array -> []
 *
 * Use these to build response schemas that always parse to a well-formed object,
 * so a missing/changed backend field degrades gracefully to a safe default.
 */

/** String field; falls back to `""` when missing or not a string. */
export const stringField = () => v.fallback(v.string(), "");

/** Nullable string field; falls back to `null` when missing or not a string. */
export const nullableStringField = () => v.fallback(v.nullable(v.string()), null);

/** Number field; falls back to `0` when missing or not a number. */
export const numberField = () => v.fallback(v.number(), 0);

/** Boolean field; falls back to `false` when missing or not a boolean. */
export const booleanField = () => v.fallback(v.boolean(), false);

/** Array field; falls back to `[]` when missing or not an array. */
export const arrayField = <TItem extends v.GenericSchema>(item: TItem) => v.fallback(v.array(item), []);

const MAX_REPORTED_ISSUES = 3;

/**
 * Summarizes issues as `path: expected <type>` lines. Deliberately omits the
 * received values (valibot embeds them in `issue.message`) so response data —
 * potentially customer PII — never leaves the browser via the Slack webhook.
 */
function formatIssuesForReport(issues: v.BaseIssue<unknown>[]): string[] {
  const formatted = issues.slice(0, MAX_REPORTED_ISSUES).map((issue) => {
    const path = issue.path?.map((segment) => String(segment.key)).join(".");
    return path ? `\`${path}\`: expected ${issue.expected}` : `expected ${issue.expected}`;
  });
  if (issues.length > MAX_REPORTED_ISSUES) {
    formatted.push(`…and ${issues.length - MAX_REPORTED_ISSUES} more`);
  }
  return formatted;
}

// Endpoints already reported this session; SWR revalidates on intervals, so a
// persistent mismatch would otherwise alert Slack on every poll.
const reportedMismatches = new Set<string>();

/**
 * Validates an API response against `schema`, returning `fallback` when the
 * top-level shape doesn't match. Unlike wrapping the whole schema in
 * `v.fallback` (which swallows mismatches silently), this logs the validation
 * issues and alerts Slack (once per endpoint per session) so backend contract
 * changes are visible in monitoring.
 *
 * Field-level mismatches are still handled by the field helpers above and do
 * not reach here; only a top-level mismatch (or a failing non-fallback nested
 * schema, e.g. an array item that isn't an object) triggers the fallback.
 */
export function safeParseResponse<TOutput>(
  schema: v.GenericSchema,
  raw: unknown,
  fallback: TOutput,
  context: string
): TOutput {
  const result = v.safeParse(schema, raw);
  if (result.success) {
    return result.output as TOutput;
  }
  console.warn(`[api] Unexpected response shape for ${context}; using fallback`, result.issues);
  if (!reportedMismatches.has(context)) {
    reportedMismatches.add(context);
    sendSlackSchemaMismatchReport({ endpoint: context, issues: formatIssuesForReport(result.issues) });
  }
  return fallback;
}
